import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { ProjectCard } from "@/components/ProjectCard";
import { SectionHeading } from "@/components/SectionHeading";
import { ChapterArt } from "@/components/ChapterArt";
import { projects } from "@/content/siteContent";

const featured = projects.filter((p) => p.featured);
const secondary = projects.filter((p) => !p.featured);

export default function Work() {
  return (
    <div data-testid="work-page">
      <header className="bg-sand">
        <div className="relative mx-auto max-w-6xl px-6 pb-16 pt-20 sm:pt-28">
          <div className="hidden sm:block">
            <ChapterArt testId="page-art-work" />
          </div>
          <Reveal>
            <p className="eyebrow">Work</p>
            <h1 className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-tighter sm:text-5xl">
              Selected work & case studies
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-muted sm:text-lg">
              A mix of corporate case studies and independent, AI-assisted projects. Only verified,
              non-confidential information is included; gaps are marked openly.
            </p>
          </Reveal>
        </div>
      </header>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <Reveal>
            <SectionHeading index="01" label="Featured" title="Case studies & featured projects" />
          </Reveal>
          <div className="mt-16 grid gap-x-12 gap-y-16 md:grid-cols-2" data-testid="featured-projects-grid">
            {featured.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <ProjectCard project={p} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-sand" data-testid="further-projects-section">
        <div className="mx-auto max-w-6xl px-6 py-24 sm:py-32">
          <Reveal>
            <SectionHeading index="02" label="Experiments & independent projects" title="Further projects & experiments" />
            <p className="mt-6 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
              Independent projects and prototypes — each labelled honestly for what it is: MVP, work in
              development, prototype, or hypothetical.
            </p>
          </Reveal>
          <div className="mt-12 divide-y divide-ink/10 border-y border-ink/10">
            {secondary.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.05}>
                <article
                  className="grid gap-4 py-10 sm:grid-cols-[220px_1fr] sm:gap-10"
                  data-testid={`secondary-project-${p.slug}`}
                >
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.2em] text-terracotta">{p.type}</p>
                    <p className="mt-3 flex items-center gap-2 text-xs text-muted">
                      <span className="h-1.5 w-1.5 rounded-full bg-sage" aria-hidden="true" />
                      {p.status}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold tracking-tight sm:text-2xl">
                      {p.caseStudy ? (
                        <Link
                          to={`/work/${p.slug}`}
                          className="editorial-link"
                          data-testid={`secondary-link-${p.slug}`}
                        >
                          {p.title}
                        </Link>
                      ) : (
                        p.title
                      )}
                    </h3>
                    <p className="mt-3 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
                      {p.summary}
                    </p>
                    {p.note && <p className="mt-3 text-xs italic text-muted">{p.note}</p>}
                    <ul className="mt-4 flex flex-wrap gap-2">
                      {p.tags.map((t) => (
                        <li key={t} className="rounded-full border border-ink/20 px-3 py-1 text-xs text-muted">
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
